import { format, parseISO, startOfDay } from 'date-fns';
import type { IApi } from '@svar-ui/react-gantt';
import type { ScheduleResource, ScheduleTask } from '../../../services/scheduleService';
import { sortTasksByCompletionDependency } from './scheduleTaskDependencyOrder';
import { formatDate as formatPacificDate } from '@/utils/displayDateTime';

export const SYNTHETIC_ID_START = 1000000;

const PLANNED_STATUSES = ['PROPOSED', 'PLANNED'];
const ACTIVE_STATUSES = ['ACTIVE', 'ACTIVE_MARKED_AS_COMPLETE', 'INTERNAL_REVIEW'];

/**
 * Bar display type: planned (blue), active (green), overdue (red), closed (gray), ongoing (year-round / as-needed).
 * Summary = employee row; summary-empty = no bar.
 */
export type BarType = 'planned' | 'active' | 'overdue' | 'closed' | 'ongoing' | 'summary' | 'summary-empty';

/** SVAR Gantt task: use either end OR duration, not both (per docs). type drives bar color. */
export interface SvarTask {
  id: number;
  text: string;
  start: Date;
  end?: Date;
  progress: number;
  type: BarType | 'task';
  parent?: number;
  open?: boolean;
  foremanArea?: string;
  position?: string;
  status?: string;
  sourceTaskId?: number;
  plannedStartDate?: string | null;
  plannedCompletionDate?: string | null;
  taskNumber?: string | null;
  project?: string | null;
  occupiedSegments?: Array<{ start: Date; end: Date }>;
  dependencies?: ScheduleTask['dependencies'];
  /** Mirrors API: year-round work shown across the visible chart range. */
  yearRoundSchedule?: boolean;
  /** Planned accomplishment (Task.identifiedAccomplishment). */
  identifiedAccomplishment?: number | null;
  actions?: string | null;
}

export type PlannedDatesMap = Map<
  number | string,
  {
    plannedStartDate: string | null;
    plannedCompletionDate: string | null;
    identifiedAccomplishment?: number | null;
    dependencies?: ScheduleTask['dependencies'] | null;
    yearRoundSchedule?: boolean;
  }
>;

export function parseSafe(dateStr: string | null): Date | null {
  if (!dateStr) return null;
  try {
    const parsed = parseISO(dateStr);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
}

/**
 * Parse planned date fields for Gantt bars: plain `YYYY-MM-DD` uses {@link parseDateOnly} (local calendar day).
 * ISO strings with time use {@link parseSafe}.
 */
export function parseSchedulePlanDate(value: string | null | undefined): Date | null {
  if (value == null) return null;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return parseDateOnly(s);
  return parseSafe(s);
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** First 10 chars yyyy-MM-dd from a plan field (handles ISO datetimes). */
export function extractPlanCalendarDateKey(value: string | null | undefined): string | null {
  if (value == null) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(value).trim());
  return m ? m[1] : null;
}

export function plannedStartAndEndSameCalendarDate(
  startField: string | null | undefined,
  endField: string | null | undefined
): boolean {
  const a = extractPlanCalendarDateKey(startField);
  const b = extractPlanCalendarDateKey(endField);
  return a != null && b != null && a === b;
}

/**
 * Same planned calendar day often parses to identical start/end instants; SVAR Gantt then draws no visible bar.
 * Force at least one day of width (capped by chart range end).
 */
export function ensureMinOneDayBarWhenSamePlannedDate(
  start: Date,
  end: Date,
  rangeEnd: Date,
  samePlannedCalendarDate: boolean
): { start: Date; end: Date } {
  if (!samePlannedCalendarDate) return { start, end };
  const s = start.getTime();
  const e = end.getTime();
  if (e - s >= MS_PER_DAY) return { start, end };
  const cap = rangeEnd.getTime();
  const candidate = Math.min(cap, s + MS_PER_DAY);
  if (candidate > s) {
    return { start, end: new Date(candidate) };
  }
  return { start, end };
}

/** Parse date-only string (YYYY-MM-DD) as local date to avoid UTC midnight shifting the day. */
export function parseDateOnly(s: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!match) return parseSafe(s);
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Format date for tooltip: accept Date or ISO/YYYY-MM-DD string; date-only as local. */
export function tooltipDate(value: Date | string | null | undefined): string {
  if (value == null) return '–';
  let d: Date | null;
  if (typeof value === 'string') {
    d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseDateOnly(value) : parseSafe(value);
  } else {
    d = value;
  }
  if (!d) return '–';
  return formatPacificDate(typeof value === 'string' ? value : d) || '–';
}

/** Overdue = planned completion date has passed (bar is red). Active/planned otherwise. */
export function barTypeForTask(t: ScheduleTask): BarType {
  if (t.yearRoundSchedule) return 'ongoing';
  const st = String(t.status ?? '').trim();
  if (st === 'CLOSED' || st === 'FINALIZED') return 'closed';
  const plannedEnd = parseSafe(t.plannedCompletionDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  if (plannedEnd && plannedEnd.getTime() < now.getTime()) return 'overdue';
  if (ACTIVE_STATUSES.includes(t.status)) return 'active';
  if (PLANNED_STATUSES.includes(t.status)) return 'planned';
  return 'active';
}

export function toSvarTask(
  t: ScheduleTask,
  syntheticId: number,
  parentId: number,
  foremanArea?: string,
  position?: string,
  chartRangeStart?: Date,
  chartRangeEnd?: Date
): SvarTask | null {
  const plannedStart = t.identifiedDate ?? t.startDate;
  let start = parseSchedulePlanDate(plannedStart) ?? parseSchedulePlanDate(t.plannedCompletionDate);
  let end = parseSchedulePlanDate(t.plannedCompletionDate) ?? parseSchedulePlanDate(t.startDate);
  if ((!start || !end) && t.yearRoundSchedule && chartRangeStart && chartRangeEnd) {
    start = startOfDay(chartRangeStart);
    end = startOfDay(chartRangeEnd);
  }
  if (!start || !end) return null;
  const startMs = start.getTime();
  const endMs = end.getTime();
  let safeEnd: Date = endMs >= startMs ? end : new Date(startMs + 7 * 24 * 60 * 60 * 1000);
  if (chartRangeEnd) {
    const sameDay = plannedStartAndEndSameCalendarDate(plannedStart, t.plannedCompletionDate);
    const adjusted = ensureMinOneDayBarWhenSamePlannedDate(start, safeEnd, chartRangeEnd, sameDay);
    start = adjusted.start;
    safeEnd = adjusted.end;
  }
  const barType = barTypeForTask(t);
  const hasDependencies = !!(t.dependencies && Array.isArray(t.dependencies.triggers) && t.dependencies.triggers.length > 0);
  const yr = t.yearRoundSchedule ? '⟳ ' : '';
  return {
    id: syntheticId,
    text: `${hasDependencies ? '⛓ ' : ''}${yr}${t.name || t.description || `Task #${t.id}`}`,
    start,
    end: safeEnd,
    // Keep progress at 0 so SVAR does not render a numeric badge on the bar (we show qty in tooltip).
    progress: 0,
    type: barType,
    parent: parentId,
    status: t.status,
    foremanArea: foremanArea ?? '–',
    position: position ?? '–',
    sourceTaskId: t.id,
    identifiedAccomplishment:
      Number.isFinite(Number(t.identifiedAccomplishment)) ? Number(t.identifiedAccomplishment) : null,
    actions: 'View task',
    plannedStartDate: plannedStart ?? null,
    plannedCompletionDate: t.plannedCompletionDate ?? null,
    dependencies: t.dependencies ?? null,
    yearRoundSchedule: t.yearRoundSchedule === true,
  };
}

export function resourcesToSvarTasks(
  resources: ScheduleResource[],
  rangeStart: Date,
  rangeEnd: Date
): SvarTask[] {
  const tasks: SvarTask[] = [];
  const resourceList = Array.isArray(resources) ? resources : [];
  let nextSyntheticId = SYNTHETIC_ID_START;

  for (const resource of resourceList) {
    const summaryId = nextSyntheticId++;
    const resourceTasks = sortTasksByCompletionDependency(
      Array.isArray(resource.tasks) ? resource.tasks : []
    );
    const childTasks: SvarTask[] = [];
    for (const task of resourceTasks) {
      const taskId = nextSyntheticId++;
      const mapped = toSvarTask(
        task,
        taskId,
        summaryId,
        resource.foremanArea?.name ?? undefined,
        resource.position ?? undefined,
        rangeStart,
        rangeEnd
      );
      if (mapped) childTasks.push(mapped);
    }
    const allStarts = childTasks.map((t) => t.start.getTime());
    const allEnds = childTasks.map((t) => (t.end ? t.end.getTime() : t.start.getTime()));
    const plannedStarts = childTasks
      .map((t) => (t.plannedStartDate ? parseDateOnly(t.plannedStartDate) : null))
      .filter((d): d is Date => d instanceof Date && !Number.isNaN(d.getTime()));
    const plannedCompletions = childTasks
      .map((t) => (t.plannedCompletionDate ? parseDateOnly(t.plannedCompletionDate) : null))
      .filter((d): d is Date => d instanceof Date && !Number.isNaN(d.getTime()));
    const summaryStart = allStarts.length > 0 ? new Date(Math.min(...allStarts)) : rangeStart;
    const summaryEnd = allEnds.length > 0 ? new Date(Math.max(...allEnds)) : rangeEnd;
    const hasChildren = childTasks.length > 0;
    const summaryPlannedStartDate =
      plannedStarts.length > 0 ? format(new Date(Math.min(...plannedStarts.map((d) => d.getTime()))), 'yyyy-MM-dd') : null;
    const summaryPlannedCompletionDate =
      plannedCompletions.length > 0
        ? format(new Date(Math.max(...plannedCompletions.map((d) => d.getTime()))), 'yyyy-MM-dd')
        : null;
    tasks.push({
      id: summaryId,
      text: `${resource.name} ${resource.lastName}`.trim(),
      start: summaryStart,
      end: summaryEnd,
      progress: 0,
      type: hasChildren ? 'summary' : 'summary-empty',
      parent: 0,
      open: hasChildren ? true : undefined,
      foremanArea: resource.foremanArea?.name ?? '–',
      position: resource.position ?? '–',
      actions: null,
      plannedStartDate: summaryPlannedStartDate,
      plannedCompletionDate: summaryPlannedCompletionDate,
    });
    tasks.push(...childTasks);
  }
  return tasks;
}

/**
 * SVAR Gantt may emit the final `update-task` with `inProgress: false` but without `task.start` / `task.end`
 * (dates only exist on the chart's internal task). Fall back to `api.getTask(id)` in that case.
 */
export function resolveDragEndRange(
  ev: {
    id: number | string;
    task?: { start?: Date | string; end?: Date | string };
    inProgress?: boolean;
  },
  api: IApi | null | undefined
): { startDate: Date; endDate: Date } | null {
  if (ev.inProgress === true) return null;
  const sid = typeof ev.id === 'string' ? parseInt(ev.id, 10) : ev.id;
  if (typeof sid !== 'number' || Number.isNaN(sid)) return null;
  let start = ev.task?.start;
  let end = ev.task?.end;
  if (start == null || end == null) {
    try {
      const t = api?.getTask?.(sid);
      start = t?.start ?? start;
      end = t?.end ?? end;
    } catch {
      /* ignore */
    }
  }
  if (start == null || end == null) return null;
  const startDate = start instanceof Date ? start : new Date(start);
  const endDate = end instanceof Date ? end : new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
  return { startDate, endDate };
}
